import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Users, 
  UserCheck, 
  Building2,
  Clock,
  Shield,
  Target,
  TrendingUp,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';

const Services: React.FC = () => {
  const services = [
    {
      icon: Users,
      title: 'Permanent Recruitment',
      description: 'Find the perfect permanent employees for your organization with our comprehensive screening process.',
      features: [
        'Executive Search & Leadership Hiring',
        'Technical & IT Recruitment',
        'Industry-Specific Expertise',
        'Background Verification & Reference Checks',
        'Cultural Fit Assessment',
        'Salary Benchmarking'
      ],
      process: [
        'Job Analysis & Requirements',
        'Candidate Sourcing & Screening',
        'Interview Coordination',
        'Reference Checks',
        'Final Selection & Onboarding'
      ]
    },
    {
      icon: Clock,
      title: 'Temporary Staffing',
      description: 'Flexible staffing solutions for short-term projects and seasonal workforce requirements.',
      features: [
        'Project-Based Hiring',
        'Seasonal Staff Solutions',
        'Event Management Staffing',
        'Quick Deployment (24-48 hours)',
        'Flexible Contract Terms',
        'On-site Management'
      ],
      process: [
        'Requirements Assessment',
        'Rapid Candidate Pool',
        'Quick Screening Process',
        'Immediate Placement',
        'Ongoing Support & Management'
      ]
    },
    {
      icon: UserCheck,
      title: 'Executive Search',
      description: 'Specialized recruitment for senior-level positions and leadership roles.',
      features: [
        'C-Level & Board Appointments',
        'Senior Management Positions',
        'Leadership Assessment Tools',
        'Market Intelligence & Mapping',
        'Confidential Search Process',
        'Succession Planning'
      ],
      process: [
        'Strategic Planning Session',
        'Market Research & Mapping',
        'Targeted Executive Search',
        'Comprehensive Assessment',
        'Board Presentation & Selection'
      ]
    },
    {
      icon: Building2,
      title: 'HR Consulting',
      description: 'Strategic human resource consulting to optimize your workforce and organizational structure.',
      features: [
        'HR Strategy Development',
        'Policy & Procedure Design',
        'Training & Development Programs',
        'Compliance & Risk Management',
        'Organizational Restructuring',
        'Performance Management Systems'
      ],
      process: [
        'Current State Analysis',
        'Strategic Planning',
        'Implementation Roadmap',
        'Change Management',
        'Ongoing Support & Review'
      ]
    }
  ];

  const industries = [
    { name: 'Technology', icon: '💻' },
    { name: 'Healthcare', icon: '🏥' },
    { name: 'Finance', icon: '💰' },
    { name: 'Manufacturing', icon: '🏭' },
    { name: 'Retail', icon: '🛍️' },
    { name: 'Construction', icon: '🏗️' },
    { name: 'Education', icon: '📚' },
    { name: 'Hospitality', icon: '🍽️' }
  ];

  const benefits = [
    {
      icon: Target,
      title: 'Precision Matching',
      description: 'Our advanced screening process ensures 95% candidate-role compatibility.'
    },
    {
      icon: Shield,
      title: 'Quality Assurance',
      description: 'Comprehensive background checks and skill verification for all placements.'
    },
    {
      icon: TrendingUp,
      title: 'Proven Results',
      description: '98% client satisfaction rate with 500+ successful placements annually.'
    },
    {
      icon: Clock,
      title: 'Time Efficiency',
      description: 'Average placement time of 2-3 weeks for permanent roles, 24-48 hours for temporary.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-white">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Our <span className="text-gradient">Services</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Comprehensive recruitment and staffing solutions tailored to meet your unique business needs. 
              From permanent placements to temporary staffing, we deliver results that drive your success.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center mb-6">
                    <service.icon className="w-8 h-8 text-primary-600" />
                  </div>
                  <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">
                    {service.title}
                  </h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Key Features:</h3>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-primary-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <Card className="p-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-6">Our Process:</h3>
                    <div className="space-y-4">
                      {service.process.map((step, stepIndex) => (
                        <div key={stepIndex} className="flex items-start">
                          <div className="w-8 h-8 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-semibold mr-4 flex-shrink-0">
                            {stepIndex + 1}
                          </div>
                          <span className="text-gray-600 pt-1">{step}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Industries We Serve
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              With deep industry knowledge and specialized expertise, we serve clients across diverse sectors.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                className="text-center p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                <div className="text-3xl mb-2">{industry.icon}</div>
                <div className="text-sm font-medium text-gray-700">{industry.name}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-display font-bold text-gray-900 mb-6">
              Why Choose Us?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our commitment to excellence and proven track record make us the preferred choice for recruitment and staffing.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-20 h-20 bg-gradient-to-br from-primary-600 to-accent-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-2xl font-display font-semibold text-gray-900 mb-4">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-accent-600">
        <div className="container-custom">
          <motion.div
            className="text-center text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-display font-bold mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Let's discuss your recruitment needs and find the perfect solution for your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-primary-600 hover:bg-gray-100 flex items-center space-x-2">
                  <span>Contact Us Today</span>
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
              <Button size="lg" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600 focus:ring-white">
                Learn More About Us
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Services;
